/* eslint-disable @atlaskit/design-system/ensure-design-token-usage, no-unused-vars */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';

import { expValEquals } from '@confluence/feature-experiments';

import type { CoverPictureId } from '../pageCoverPictureTypes.ts';
import {
	COVER_IMAGE_FIXED_HEIGHT,
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE,
} from '../pageCoverPictureConstants.ts';

import { ImageWrapper } from './ImageWrapper.tsx';

type AllowedExternalLoadingDivProps = {
	hasAllowedExternalImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
};

const styles = cssMap({
	base: {
		display: 'flex',
		width: '100%',
		position: 'absolute',
	},
	loading: {
		position: 'relative',
		width: '100%',
		backgroundColor: token('color.background.accent.gray.subtlest'),
	},
	noOpacity: {
		opacity: 0,
	},
	opacity: {
		opacity: 1,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AllowedExternalLoadingDivStyled = styled.div<
	AllowedExternalLoadingDivProps & { smartSpaceOverviewHeight?: number }
>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) =>
		props.smartSpaceOverviewHeight
			? `${props.smartSpaceOverviewHeight}px`
			: `${props.dynamicCoverImageHeight}px`,
	width: '100%',
	background: token('color.background.accent.gray.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.hasAllowedExternalImageLoaded ? 0 : 1),
	borderRadius: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AllowedExternalWrapperStyled = styled.div<{
	hasAllowedExternalImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
}>({
	display: 'flex',
	width: '100%',
	position: 'absolute',
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => {
		if (props.smartSpaceOverviewHeight) {
			return `${props.smartSpaceOverviewHeight}px`;
		}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		if (expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true)) {
			if (props.isFixedWidthImageOption) {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				return `${COVER_IMAGE_FIXED_HEIGHT}px`;
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			return `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}px`;
		}
		return `${props.dynamicCoverImageHeight}px`;
	},
});

export type AllowedExternalCoverPictureProps = {
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	coverPictureDataPosition?: number;
	coverPictureDataId?: string;
	isRoundedCorners?: boolean;
};

const getMinHeightStyle = (
	isFixedWidthImageOption?: boolean,
	smartSpaceOverviewHeight?: number,
	dynamicCoverImageHeight?: number,
) => {
	if (smartSpaceOverviewHeight) {
		return `${smartSpaceOverviewHeight}px`;
	}
	if (expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true)) {
		if (isFixedWidthImageOption) {
			return `${COVER_IMAGE_FIXED_HEIGHT}px`;
		}
		return `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}px`;
	}
	return dynamicCoverImageHeight ? `${dynamicCoverImageHeight}px` : undefined;
};

const AllowedExternalWrapper = ({
	hasAllowedExternalImageLoaded,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	children,
}: {
	hasAllowedExternalImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	smartSpaceOverviewHeight?: number;
	children: React.ReactNode;
}) => {
	if (expValEquals('confluence_page_cover_picture_css_migration', 'isEnabled', true)) {
		return (
			<div
				css={styles.base}
				style={{
					minHeight: getMinHeightStyle(
						isFixedWidthImageOption,
						smartSpaceOverviewHeight,
						dynamicCoverImageHeight,
					),
				}}
				data-testid="unsplash-wrapper-css" // TODO: rename this to unsplash-wrapper when cleaning up confluence_page_cover_picture_css_migration
			>
				{children}
			</div>
		);
	}

	return (
		<AllowedExternalWrapperStyled
			hasAllowedExternalImageLoaded={hasAllowedExternalImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			isFixedWidthImageOption={isFixedWidthImageOption}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-wrapper"
		>
			{children}
		</AllowedExternalWrapperStyled>
	);
};

const AllowedExternalLoadingDiv = ({
	hasAllowedExternalImageLoaded,
	dynamicCoverImageHeight,
	smartSpaceOverviewHeight,
}: {
	hasAllowedExternalImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	smartSpaceOverviewHeight?: number;
}) => {
	if (expValEquals('confluence_page_cover_picture_css_migration', 'isEnabled', true)) {
		return (
			<div
				css={[styles.loading, hasAllowedExternalImageLoaded ? styles.noOpacity : styles.opacity]}
				style={{
					height: smartSpaceOverviewHeight
						? `${smartSpaceOverviewHeight}px`
						: dynamicCoverImageHeight
							? `${dynamicCoverImageHeight}px`
							: undefined,
				}}
				data-testid="unsplash-loading-div-css" // TODO: rename this to unsplash-loading-div when cleaning up confluence_page_cover_picture_css_migration
			/>
		);
	}

	return (
		<AllowedExternalLoadingDivStyled
			hasAllowedExternalImageLoaded={hasAllowedExternalImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-loading-div"
		/>
	);
};

export const AllowedExternalCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
	smartSpaceOverviewHeight,
	coverPictureDataPosition,
	coverPictureDataId,
	isRoundedCorners,
}: AllowedExternalCoverPictureProps) => {
	const [hasAllowedExternalImageLoaded, setHasAllowedExternalImageLoaded] =
		useState<boolean>(false);

	return (
		<AllowedExternalWrapper
			hasAllowedExternalImageLoaded={hasAllowedExternalImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			isFixedWidthImageOption={isFixedWidthImageOption}
			smartSpaceOverviewHeight={smartSpaceOverviewHeight}
			data-testid="unsplash-wrapper"
		>
			{!expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true) && (
				<AllowedExternalLoadingDiv
					hasAllowedExternalImageLoaded={hasAllowedExternalImageLoaded}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					smartSpaceOverviewHeight={smartSpaceOverviewHeight}
					data-testid="unsplash-loading-div"
				/>
			)}
			<ImageWrapper
				data-testid="unsplash-cover-picture"
				id="actual-cover-picture"
				data-vc="unsplash-cover-picture-editor"
				src={coverPicture.current.draft?.value.id ?? coverPictureDataId}
				coverPicturePosition={
					coverPicture.current.draft?.value.position ?? coverPictureDataPosition
				}
				onLoad={() => {
					setHasAllowedExternalImageLoaded(true);
				}}
				isFixedWidthImageOption={isFixedWidthImageOption}
				smartSpaceOverviewHeight={smartSpaceOverviewHeight}
				isRoundedCorners={isRoundedCorners}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className="cover-picture-wrapper-dangerously-set-inner-html"
			/>
		</AllowedExternalWrapper>
	);
};
